/**
 * HeaderMenu · Operación Feten BUG-04
 * Avatar tap → sheet con Mi perfil / Configuración / Cerrar sesión.
 * Integrado en AppLayout — visible en todas las surfaces protegidas.
 * Logout accesible en máximo 2 taps desde cualquier pantalla.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { User, Settings, LogOut, X } from 'lucide-react'
import useAuthStore from '../../store/authStore'

function getInitials(name) {
  if (!name) return 'PL'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export default function HeaderMenu() {
  const { profile, signOut } = useAuthStore()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [confirmLogout, setConfirmLogout] = useState(false)

  const initials = getInitials(profile?.full_name)

  async function handleLogout() {
    setConfirmLogout(false)
    setOpen(false)
    await signOut()
    navigate('/', { replace: true })
  }

  return (
    <>
      {/* Avatar trigger — fixed top-right inside the layout container */}
      <button
        aria-label="Menú de usuario"
        onClick={() => { setOpen(true); setConfirmLogout(false) }}
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 90,
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'var(--pl-terracota)',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
          fontSize: 13,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          letterSpacing: '0.03em',
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        }}
        data-testid="header-menu-trigger"
      >
        {initials}
      </button>

      {/* Backdrop + bottom sheet */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => { setOpen(false); setConfirmLogout(false) }}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.25)',
                zIndex: 95,
              }}
            />

            {/* Sheet */}
            <motion.div
              key="sheet"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'fixed',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: 430,
                background: 'var(--bg-elevated)',
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -4px 32px rgba(0,0,0,0.10)',
                zIndex: 96,
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}
            >
              {/* Handle */}
              <div style={{
                width: 36, height: 4, borderRadius: 2,
                background: 'var(--border)',
                margin: '12px auto 0',
              }} />

              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px 12px',
                borderBottom: '1px solid var(--border)',
              }}>
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-sans)' }}>
                    {profile?.full_name || 'Mi cuenta'}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-sans)' }}>
                    {profile?.email || ''}
                  </p>
                </div>
                <button
                  onClick={() => { setOpen(false); setConfirmLogout(false) }}
                  aria-label="Cerrar menú"
                  style={{
                    background: 'var(--pl-subtle)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <X size={16} color="var(--text-muted)" strokeWidth={2} />
                </button>
              </div>

              {/* Menu items */}
              {!confirmLogout ? (
                <nav style={{ padding: '8px 0 24px' }}>
                  <MenuItem
                    icon={<User size={18} color="var(--text-soft)" strokeWidth={1.5} />}
                    label="Mi perfil"
                    onClick={() => { setOpen(false); navigate('/perfil') }}
                  />
                  <MenuItem
                    icon={<Settings size={18} color="var(--text-soft)" strokeWidth={1.5} />}
                    label="Configuración"
                    onClick={() => { setOpen(false); navigate('/perfil') }}
                  />
                  <div style={{ height: 1, background: 'var(--border)', margin: '8px 20px' }} />
                  <MenuItem
                    icon={<LogOut size={18} color="var(--pl-danger)" strokeWidth={1.5} />}
                    label="Cerrar sesión"
                    labelColor="var(--pl-danger)"
                    onClick={() => setConfirmLogout(true)}
                    data-testid="header-menu-logout"
                  />
                </nav>
              ) : (
                /* Logout confirmation */
                <div style={{ padding: '20px 20px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <p style={{
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 500,
                    color: 'var(--text)',
                    fontFamily: 'var(--font-sans)',
                    textAlign: 'center',
                  }}>
                    ¿Cerrar sesión?
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-sans)',
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}>
                    Tendrás que volver a introducir tu email y contraseña.
                  </p>
                  <button
                    onClick={handleLogout}
                    data-testid="header-menu-confirm-logout"
                    style={{
                      marginTop: 4,
                      height: 52,
                      background: 'var(--pl-danger)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      fontFamily: 'var(--font-sans)',
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Sí, cerrar sesión
                  </button>
                  <button
                    onClick={() => setConfirmLogout(false)}
                    style={{
                      height: 48,
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      fontFamily: 'var(--font-sans)',
                      fontSize: 15,
                      cursor: 'pointer',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

function MenuItem({ icon, label, onClick, labelColor, ...rest }) {
  return (
    <button
      onClick={onClick}
      {...rest}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        padding: '13px 20px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <span style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 15,
        fontWeight: 400,
        color: labelColor || 'var(--text)',
      }}>
        {label}
      </span>
    </button>
  )
}
